import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    placeholder: String,
    allowEmptyOption: Boolean,
    multiple: Boolean,
    closeAfterSelect: Boolean,
    plugins: Array,
    valueField: String,
    labelField: String,
    searchField: String,
    create: Boolean,
    maxItems: Number,
    minLength: Number,
    ariaLabel: String,
    ariaDescribedBy: String
  }

  connect() {
    this.boundClickOutside = this.handleClickOutside.bind(this)
    this.initializeTomSelect()
  }

  initializeTomSelect() {
    const config = {
      plugins: this.pluginsValue || ['remove_button'],
      valueField: this.valueFieldValue || 'value',
      labelField: this.labelFieldValue || 'text',
      searchField: this.searchFieldValue || 'text',
      closeAfterSelect: this.closeAfterSelectValue !== false,
      placeholder: this.placeholderValue || 'Search and select...',
      allowEmptyOption: this.allowEmptyOptionValue || false,
      create: this.createValue || false,
      maxItems: this.maxItemsValue || null,
      minLength: this.minLengthValue || 2,
      onItemAdd: function(value, item) {
        // Clear the search input after adding an item
        this.setTextboxValue('')
      },
      onItemRemove: function(value, item) {
        // Optional: Add any cleanup logic here
      },
      load: (query, callback) => this.searchItems(query, callback),
      render: {
        option: function(data, escape) {
          return `<div class="theme-list-group-item theme-list-group-item-action">
            <div class="theme-list-group-item-title">${escape(data.text || data.label || data.name)}</div>
            ${data.subtitle ? `<div class="theme-list-group-item-subtitle theme-text-secondary">${escape(data.subtitle)}</div>` : ''}
          </div>`
        },
        item: function(data, escape) {
          return `<div class="theme-list-group-item">
            <span class="theme-list-group-item-text">${escape(data.text || data.label || data.name)}</span>
          </div>`
        },
        no_results: function(data, escape) {
          return `<div class="theme-list-group-item theme-list-group-item-action">
            <div class="theme-list-group-item-title theme-text-secondary">No results found</div>
            <div class="theme-list-group-item-subtitle theme-text-secondary">Try a different search term or check your spelling</div>
          </div>`
        },
        loading: function(data, escape) {
          return `<div class="theme-list-group-item">
            <span class="theme-list-group-item-text">Loading...</span>
          </div>`
        }
      }
    }

    // Initialize TomSelect
    this.tomSelect = new TomSelect(this.element, config)

    // Add custom CSS classes for styling
    this.element.classList.add('tom-select-enhanced')
    
    // Add accessibility attributes
    this.addAccessibilityAttributes()
    
    // Add event listeners for better UX
    this.addEventListeners()
  }

  async searchItems(query, callback) {
    // Set minimum length for search
    if (!query || query.length < (this.minLengthValue || 2)) {
      callback([])
      return
    }

    if (!this.urlValue) {
      callback([])
      return
    }

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        const items = Array.isArray(data) ? data : (data.items || [])
        callback(items)
      } else {
        callback([])
      }
    } catch (error) {
      callback([])
    }
  }



  addEventListeners() {
    // Add keyboard navigation improvements
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.tomSelect.isOpen) {
        e.preventDefault()
        // Let TomSelect handle the selection
      }
      
      // Add Escape key support to close dropdown
      if (e.key === 'Escape' && this.tomSelect.isOpen) {
        e.preventDefault()
        this.tomSelect.close()
      }
      
      // Add Tab key support for better navigation
      if (e.key === 'Tab') {
        // Allow default tab behavior but ensure TomSelect stays open
        e.preventDefault()
        this.tomSelect.focus()
      }
    })

    // Add focus improvements with CSS class toggling (no inline styles)
    this.element.addEventListener('focus', () => {
      this.element.classList.add('tom-select-focused')
    })

    this.element.addEventListener('blur', () => {
      this.element.classList.remove('tom-select-focused')
    })
    
    // Add click outside to close functionality (using stored reference for cleanup)
    document.addEventListener('click', this.boundClickOutside)
  }

  handleClickOutside(e) {
    if (!this.element.contains(e.target) && this.tomSelect.isOpen) {
      this.tomSelect.close()
    }
  }

  // Method to programmatically add an item
  addItem(itemId, itemText) {
    if (this.tomSelect) {
      this.tomSelect.addOption({
        value: itemId,
        text: itemText
      })
      this.tomSelect.addItem(itemId)
    }
  }

  // Method to clear all selections
  clearAll() {
    if (this.tomSelect) {
      this.tomSelect.clear()
    }
  }

  // Method to get selected item IDs
  getSelectedItemIds() {
    if (this.tomSelect) {
      return this.tomSelect.getValue()
    }
    return []
  }

  // Method to set selected items
  setSelectedItems(itemIds) {
    if (this.tomSelect && Array.isArray(itemIds)) {
      this.tomSelect.setValue(itemIds)
    }
  }

  // Method to add options programmatically
  addOptions(options) {
    if (this.tomSelect && Array.isArray(options)) {
      options.forEach(option => {
        this.tomSelect.addOption(option)
      })
    }
  }

  // Method to remove an option
  removeOption(value) {
    if (this.tomSelect) {
      this.tomSelect.removeOption(value)
    }
  }

  // Method to refresh the dropdown
  refresh() {
    if (this.tomSelect) {
      this.tomSelect.refreshOptions()
    }
  }

  // Method to enable/disable the select
  setEnabled(enabled) {
    if (this.tomSelect) {
      if (enabled) {
        this.tomSelect.enable()
      } else {
        this.tomSelect.disable()
      }
    }
  }

  // Method to add accessibility attributes
  addAccessibilityAttributes() {
    // Add ARIA labels for better screen reader support
    if (this.ariaLabelValue) {
      this.element.setAttribute('aria-label', this.ariaLabelValue)
    }
    
    if (this.ariaDescribedByValue) {
      this.element.setAttribute('aria-describedby', this.ariaDescribedByValue)
    }
    
    // Add role attribute for screen readers
    this.element.setAttribute('role', 'combobox')
    
    // Add required attributes for accessibility
    this.element.setAttribute('aria-expanded', 'false')
    this.element.setAttribute('aria-haspopup', 'listbox')
    
    // Add autocomplete attribute for better keyboard navigation
    this.element.setAttribute('autocomplete', 'off')
  }

  disconnect() {
    // Clean up document-level event listener
    if (this.boundClickOutside) {
      document.removeEventListener('click', this.boundClickOutside)
    }

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
    
    // Clean up accessibility attributes
    if (this.ariaLabelValue) {
      this.element.removeAttribute('aria-label')
    }
    
    if (this.ariaDescribedByValue) {
      this.element.removeAttribute('aria-describedby')
    }
    
    this.element.removeAttribute('role')
    this.element.removeAttribute('aria-expanded')
    this.element.removeAttribute('aria-haspopup')
    this.element.removeAttribute('autocomplete')
  }
}
